/**
 * The chosen variation's price, times the quantity.
 *
 * On a product with choices -- a cake's size and diet -- the price box above
 * the quantity shows the chosen variation's price, and kept showing it for
 * one whatever the quantity: two 45 € cakes still read 45,00 € beside the
 * button. Here the box shows what the quantity comes to, with the price of
 * one under it ("2 × 45,00 €"); at one it is WooCommerce's own again. On a
 * product without choices -- three allergy surcharges for a family -- the
 * same goes for the price under its name.
 *
 * WooCommerce rewrites the box each time a variation is chosen and then
 * fires show_variation, a jQuery event, with the variation and its price; the
 * quantity is read from the field whenever it, or the stepper beside it,
 * changes. The store's price format comes from lib/woocommerce.php
 * (window.diakonPriceFormat), so the figures read as WooCommerce's own.
 */

/**
 * A price as the store writes it: "1 234,50 €".
 *
 * @param {number} amount The amount.
 * @param {Object} format window.diakonPriceFormat.
 * @return {string} The price.
 */
function formatPrice(amount, format) {
	const [whole, fraction] = Math.abs(amount)
		.toFixed(format.decimals)
		.split('.');
	const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, format.thousands);
	const number =
		(amount < 0 ? '-' : '') +
		grouped +
		(fraction ? format.decimal + fraction : '');

	return format.pattern
		.replace('%1$s', format.symbol)
		.replace('%2$s', number);
}

/**
 * Writes the total for a quantity into a price box, with the price of one
 * under it; at one, puts back what was there.
 *
 * @param {Element} box      The price box.
 * @param {number}  unit     The price of one.
 * @param {number}  quantity The quantity.
 * @param {string}  original The box's own content.
 * @param {Object}  format   window.diakonPriceFormat.
 */
function renderTotal(box, unit, quantity, original, format) {
	if (quantity <= 1) {
		box.innerHTML = original;
		return;
	}

	const total = document.createElement('span');
	total.className = 'woocommerce-Price-amount amount';
	total.textContent = formatPrice(unit * quantity, format);

	const each = document.createElement('span');
	each.className = 'diakon-price-each';
	each.textContent = `${quantity} × ${formatPrice(unit, format)}`;

	box.replaceChildren(total, each);
}

/**
 * The form's quantity, and a call whenever it changes.
 *
 * @param {HTMLFormElement} form     The add to cart form.
 * @param {Function}        onChange Called with the quantity.
 */
function watchQuantity(form, onChange) {
	const read = () => {
		const field = form.querySelector('input.qty');

		if (field && field.type !== 'hidden') {
			onChange(Math.max(1, parseInt(field.value, 10) || 1));
		}
	};

	form.addEventListener('input', (event) => {
		if (event.target.matches('input.qty')) {
			read();
		}
	});

	form.addEventListener('change', (event) => {
		if (event.target.matches('input.qty')) {
			read();
		}
	});

	// The stepper's − and + set the field without an event of their own
	// reaching here; read it once they have.
	form.addEventListener('click', (event) => {
		if (
			event.target.closest(
				'.wc-block-components-quantity-selector__button'
			)
		) {
			window.requestAnimationFrame(read);
		}
	});

	return read;
}

/**
 * A product with choices: the chosen variation's box above the quantity,
 * rewritten by WooCommerce each time a variation is chosen.
 *
 * @param {HTMLFormElement} form   The variations form.
 * @param {Object}          format window.diakonPriceFormat.
 */
function initVariationForm(form, format) {
	const $ = window.jQuery;
	let unit = null;
	let original = '';
	let quantity = 1;

	const render = () => {
		const box = form.querySelector('.woocommerce-variation-price .price');

		if (box && unit !== null) {
			renderTotal(box, unit, quantity, original, format);
		}
	};

	const read = watchQuantity(form, (value) => {
		quantity = value;
		render();
	});

	$(form).on('show_variation', (event, variation) => {
		const box = form.querySelector('.woocommerce-variation-price .price');

		unit = Number(variation.display_price);
		original = box ? box.innerHTML : '';
		read();
		render();
	});

	$(form).on('hide_variation', () => {
		unit = null;
	});

	// A variation chosen by default may have been shown before the above was
	// listening; WooCommerce shows it again on check_variations.
	$(form).trigger('check_variations');
}

/**
 * A product without choices: its own price under its name, the only price
 * on the page. Its price of one comes from lib/woocommerce.php
 * (window.diakonStore.productPrice).
 *
 * @param {HTMLFormElement} form   The add to cart form.
 * @param {Object}          format window.diakonPriceFormat.
 * @param {number}          unit   The price of one.
 */
function initSimpleForm(form, format, unit) {
	const box = document.querySelector(
		'.wp-block-woocommerce-product-price[data-is-descendent-of-single-product-template="true"] .wc-block-components-product-price'
	);

	if (!box) {
		return;
	}

	const original = box.innerHTML;

	const read = watchQuantity(form, (quantity) =>
		renderTotal(box, unit, quantity, original, format)
	);

	// The quantity may not start at one: after an add to cart the page
	// reloads with the quantity just added still in the field (WooCommerce
	// keeps the posted one), and a page from the back-forward cache keeps
	// whatever was typed.
	read();
	window.addEventListener('pageshow', read);
}

export function initProductQuantityTotal() {
	const format = window.diakonPriceFormat;

	if (!format) {
		return;
	}

	if (window.jQuery) {
		document
			.querySelectorAll('form.variations_form')
			.forEach((form) => initVariationForm(form, format));
	}

	const unit = window.diakonStore?.productPrice;
	const simple = document.querySelector(
		'.wp-block-woocommerce-add-to-cart-form form.cart:not(.variations_form)'
	);

	if (simple && typeof unit === 'number') {
		initSimpleForm(simple, format, unit);
	}
}
